import React, { useState, useEffect } from 'react';
import { Navbar, Nav, Container, Image, Dropdown } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSun,
  faMoon,
  faDownload,
  faChevronDown,
  faHouse,
  faReceipt,
  faChartPie,
  faEllipsisH,
} from '@fortawesome/free-solid-svg-icons';

const Navigation = () => {
  const { isAuthenticated, currentUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  useEffect(() => {
    const checkIfInstalled = () =>
      window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    const handleAppInstalled = () => {
      setShowInstallButton(false);
      setDeferredPrompt(null);
    };

    if (!checkIfInstalled()) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.addEventListener('appinstalled', handleAppInstalled);
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      };
    }
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  if (location.pathname === '/login' || location.pathname === '/auth-callback') {
    return null;
  }

  const isActive = (path, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <>
      <div className="app-topbar">
        <Navbar expand="lg" className="py-0">
          <Container>
            <Dropdown className="app-brand-dropdown">
              <Dropdown.Toggle
                as={Navbar.Brand}
                variant="link"
                className="text-decoration-none app-brand-toggle"
                aria-label="Select application"
              >
                <div className="d-flex align-items-center gap-2">
                  <img
                    src="/logo192.png"
                    alt=""
                    className="app-brand-logo"
                    width="36"
                    height="36"
                  />
                  <span className="brand-wordmark">Budgie</span>
                  <FontAwesomeIcon icon={faChevronDown} size="xs" className="opacity-50" />
                </div>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item as={Link} to="/" onClick={() => document.activeElement?.blur()}>
                  <img src="/logo192.png" alt="" className="app-brand-logo-sm me-2" width="20" height="20" />
                  Budgie · budgie.ducklab.net
                </Dropdown.Item>
                <Dropdown.Item href="https://jabtrack.ducklab.net" target="_blank" rel="noopener noreferrer">
                  JabTrack · jabtrack.ducklab.net
                </Dropdown.Item>
                <Dropdown.Item href="https://graphs.ducklab.net" target="_blank" rel="noopener noreferrer">
                  Graphs · graphs.ducklab.net
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {isAuthenticated && (
              <Nav className="app-desktop-nav flex-row">
                <Nav.Link as={Link} to="/" className={isActive('/', true) ? 'active' : ''}>
                  Home
                </Nav.Link>
                <Nav.Link as={Link} to="/expenses" className={isActive('/expenses') ? 'active' : ''}>
                  Expenses
                </Nav.Link>
                <Nav.Link as={Link} to="/statistics" className={isActive('/statistics') ? 'active' : ''}>
                  Statistics
                </Nav.Link>
                <Nav.Link as={Link} to="/analysis" className={isActive('/analysis') ? 'active' : ''}>
                  Analysis
                </Nav.Link>
              </Nav>
            )}

            <div className="app-topbar-actions">
              {showInstallButton && (
                <button
                  type="button"
                  className="app-icon-btn"
                  onClick={handleInstallClick}
                  title="Install Budgie"
                  aria-label="Install Budgie"
                >
                  <FontAwesomeIcon icon={faDownload} />
                </button>
              )}

              <button
                type="button"
                className="app-icon-btn"
                onClick={toggleTheme}
                title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                aria-label="Toggle theme"
              >
                <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
              </button>

              {isAuthenticated && currentUser && (
                <Dropdown align="end">
                  <Dropdown.Toggle
                    as="button"
                    className="app-icon-btn border-0 p-0 overflow-hidden"
                    style={{ width: '2.4rem', height: '2.4rem' }}
                    aria-label="Account menu"
                  >
                    {currentUser.profilePicture ? (
                      <Image
                        src={currentUser.profilePicture}
                        roundedCircle
                        width="38"
                        height="38"
                        alt={currentUser.displayName}
                      />
                    ) : (
                      <span className="small fw-semibold">
                        {(currentUser.displayName || 'U').slice(0, 1)}
                      </span>
                    )}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Header>{currentUser.displayName}</Dropdown.Header>
                    <Dropdown.Item as={Link} to="/profile">
                      Profile
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/salary">
                      Salary
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item as={Link} to="/categories">
                      Categories
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/labels">
                      Labels
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={logout}>Log out</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </Container>
        </Navbar>
      </div>

      {isAuthenticated && (
        <nav className="app-bottom-nav" aria-label="Primary">
          <Link to="/" className={isActive('/', true) ? 'active' : ''}>
            <FontAwesomeIcon icon={faHouse} />
            Home
          </Link>
          <Link to="/expenses" className={isActive('/expenses') ? 'active' : ''}>
            <FontAwesomeIcon icon={faReceipt} />
            Expenses
          </Link>
          <Link
            to="/statistics"
            className={isActive('/statistics') || isActive('/analysis') ? 'active' : ''}
          >
            <FontAwesomeIcon icon={faChartPie} />
            Stats
          </Link>
          <Link to="/profile" className={isActive('/profile') || isActive('/salary') ? 'active' : ''}>
            <FontAwesomeIcon icon={faEllipsisH} />
            More
          </Link>
        </nav>
      )}
    </>
  );
};

export default Navigation;
